"use client";

import type { CSSProperties } from "react";
import { Chrome } from "@/components/ui/tls/chrome";
import { Mono } from "@/components/ui/tls/mono";
import { ModelBadge } from "@/components/ui/tls/model-badge";
import { visibleDownloadCount } from "@/lib/format-count";
import { fmtDurationPrecise, fmtFileSize } from "@/lib/tls-format";
import { TESLA_FACTS } from "@/lib/tesla-facts";
import { type Sound } from "@/lib/supabase";
import { PostDownloadPanel } from "@/components/ui/post-download-panel";
import { InstallSteps } from "@/components/ui/tls/install-steps";

export interface SpecSheetPanelProps {
  sound: Sound;
  liveDownloads: number;
  downloadCompleted: boolean;
  installHint: string | null;
  /**
   * What the Download hands over. A long sound is cut at download, so its
   * catalog duration and size describe a file the visitor never receives;
   * an empty `sizeLabel` drops the size row.
   */
  delivered?: { seconds: number; sizeLabel: string };
  onShare: () => void;
  onCreateShareCard: () => void;
  /**
   * TES-1238: on the sound-detail page the inline <PostDownloadReceipt/> is now
   * the single post-download surface, so the sidebar's fuller PostDownloadPanel
   * is suppressed to avoid a duplicate confirmation + install-step block. Other
   * consumers (e.g. Chime Studio) keep it by leaving this at its default.
   */
  showPostDownloadPanel?: boolean;
}

/**
 * Ruling C: attribution values are prose in the one family — body scale,
 * body weight (500 as of ruling A), ink-meta tone. Body-sm rather than body
 * because the credit sits at the foot of a dense sidebar column; it is the
 * body ROLE, one step down the ladder, not the label role.
 */
const attributionValueStyle: CSSProperties = {
  font: "var(--tls-body-sm)",
  color: "var(--tls-fg-subtle)",
};

/**
 * Right-column spec sheet panel extracted from sound-player-client.tsx.
 * The caller keeps the outer `m.div variants={itemVariants}` wrapper; this
 * component renders the blocks that live inside it, byte-identical.
 */
export function SpecSheetPanel({
  sound,
  liveDownloads,
  downloadCompleted,
  installHint,
  delivered,
  onShare,
  onCreateShareCard,
  showPostDownloadPanel = true,
}: SpecSheetPanelProps) {
  const downloadCount = visibleDownloadCount(liveDownloads);
  const compatibleModels =
    sound.compatible_models && sound.compatible_models.length > 0
      ? sound.compatible_models
      : ["S", "3", "X", "Y", "CT"];
  const allModels = ["S", "3", "X", "Y", "CT"];
  // F-8: the SOURCE label is followed by the value; some catalog rows store
  // the value already prefixed ("Source: Iron Man…"), which doubled the word.
  const sourceReference = sound.source_reference
    ?.replace(/^\s*source\s*[:\-\u2013\u2014]\s*/i, "")
    .trim();

  return (
    <>
      <aside
        style={{
          paddingTop: 20,
          borderTop: "1px solid var(--tls-border-subtle)",
        }}
      >
        <div className="tlsr-section-tick" style={{ marginBottom: 14 }}>
          <Chrome>spec sheet</Chrome>
        </div>

        {/* Spec sheet (§5.5). Duration · Size · Downloads with a
            Details disclosure for format/rate/bit-depth. */}
        {[
          {
            l: "duration",
            v:
              (delivered ? delivered.seconds : sound.duration)
                ? fmtDurationPrecise(delivered ? delivered.seconds : sound.duration)
                : "N/A",
          },
          {
            l: "file size",
            v: (delivered ? delivered.sizeLabel : fmtFileSize(sound.size)) || null,
          },
          // 1 to 24 is omitted, zero keeps its state (lib/format-count.ts).
          {
            l: "downloads",
            v: downloadCount
              ? downloadCount.isZero
                ? `${downloadCount.value} ${downloadCount.unit.toLowerCase()}`
                : downloadCount.value
              : null,
          },
        ]
          .filter(row => row.v !== null)
          .map(row => (
          <div
            key={row.l}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 0",
              borderBottom: "1px solid var(--tls-border-subtle)",
            }}
          >
            <Chrome>{row.l}</Chrome>
            <Mono size={14} color="var(--tls-fg-strong)">
              {row.v}
            </Mono>
          </div>
        ))}

        {/* Details disclosure (§5.5) */}
        <details className="group tlsr-details-animate" style={{ marginTop: 4 }}>
            {/* eslint-disable-next-line react-doctor/no-tiny-text -- details disclosure label intentionally uses 11px */}
            <summary
              className="cursor-pointer select-none list-none flex items-center justify-between py-2"
              style={{ color: "var(--tls-fg-muted)", fontSize: 11 }}
            >
              <Chrome>details</Chrome>
              <svg
                className="size-3 transition-transform group-open:rotate-180"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </summary>
            <div>
              {[
                { l: "format", v: (sound.format?.toUpperCase() || "WAV") + " / PCM" },
                { l: "sample rate", v: "44.1 kHz" },
                { l: "bit depth", v: "16-bit" },
              ].map(row => (
                <div
                  key={row.l}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid var(--tls-border-subtle)",
                  }}
                >
                  <Chrome>{row.l}</Chrome>
                  <Mono size={14} color="var(--tls-fg-strong)">{row.v}</Mono>
                </div>
              ))}
              {sound.creator_name && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--tls-border-subtle)" }}>
                  <Chrome>submitted by</Chrome>
                  {/* Ruling C: a person's name is prose, not a spec value. */}
                  <span style={{ ...attributionValueStyle, display: "block", marginTop: 4 }}>
                    {sound.creator_name}
                  </span>
                </div>
              )}
            </div>
          </details>

        {/* Compatible models (§5.6). Factual compatibility remains inline;
            status is not a green success chip. */}
        <Chrome style={{ display: "block", margin: "20px 0 10px" }}>compatible models</Chrome>
        {compatibleModels.length === 5 ? (
          <Mono size={14} color="var(--tls-fg-strong)">
            {TESLA_FACTS.compatibility.short}
          </Mono>
        ) : (
          <>
            <Mono size={11} color="var(--tls-fg-subtle)" style={{ display: "block", marginBottom: 8 }}>
              Works on {compatibleModels.length} Tesla model{compatibleModels.length !== 1 ? "s" : ""}
            </Mono>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {allModels.map(m => (
                <span
                  key={m}
                  style={!compatibleModels.includes(m) ? { opacity: 0.4 } : undefined}
                >
                  <ModelBadge model={m} active={compatibleModels.includes(m)} />
                </span>
              ))}
            </div>
          </>
        )}

        {/* Post-download CTAs */}
        {downloadCompleted && showPostDownloadPanel && (
          <PostDownloadPanel
            soundId={sound.id}
            soundTitle={sound.title}
            className="mt-4"
            onShare={onShare}
            onCreateShareCard={onCreateShareCard}
          />
        )}
      </aside>

      {/* The install path, open and in the server HTML. Once the download is
          done the receipt next to the transport carries the steps, so this
          one steps aside. */}
      {!downloadCompleted && (
        <section aria-labelledby="install-steps-heading" className="mt-8">
          <h3 id="install-steps-heading" className="tls-h4 m-0 mb-4">
            How to install
          </h3>
          {installHint && (
            // An annotation: weight and ink-muted tone carry it, no rule or colour.
            <p className="m-0 mb-4 text-sm font-semibold text-[var(--tls-fg-muted)]">{installHint}</p>
          )}
          <InstallSteps variant="condensed" />
        </section>
      )}
      {/* Creator / source credit (§5.8).
          Owner ruling C (2026-08-14): this block is prose, not code. It used
          to set five lines of a person's name and a source citation in the
          monospace meta face, which read as a machine readout of something
          a human wrote. The label lines stay on the 11px label token
          (<Chrome>); the values are body-scale prose in ink-meta. */}
      {(sound.creator_name || sourceReference) && (
        <div
          className="mt-4 py-3"
          style={{
            borderTop: "1px solid var(--tls-border-subtle)",
          }}
        >
          {sound.creator_name && (
            <div>
              <Chrome>submitted by</Chrome>{" "}
              <span style={attributionValueStyle}>{sound.creator_name}</span>
            </div>
          )}
          {sourceReference && (
            <div style={{ marginTop: sound.creator_name ? 4 : 0 }}>
              <Chrome>source</Chrome>{" "}
              <span style={attributionValueStyle}>{sourceReference}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
